import React, { useState, useRef, Component } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { Clock, Brain, Zap, Check, X, BookOpen, Shield, Menu, Star } from 'lucide-react';
// --- Components ---
const FadeIn = ({
  children,
  delay = 0,
  className = ''
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) => {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-50px'
  });
  return <motion.div ref={ref} initial={{
    opacity: 0,
    y: 20
  }} animate={isInView ? {
    opacity: 1,
    y: 0
  } : {
    opacity: 0,
    y: 20
  }} transition={{
    duration: 0.6,
    delay,
    ease: 'easeOut'
  }} className={className} data-id="element-2">
      {children}
    </motion.div>;
};
const Button = ({
  children,
  variant = 'primary',
  className = '',
  href,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary';
  href?: string;
}) => {
  const baseStyles = 'inline-flex items-center justify-center px-8 py-4 rounded-full font-bold text-lg transition-all duration-300 transform hover:scale-105 active:scale-95 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-zinc-900';
  const variants = {
    primary: 'bg-[#00D9C0] text-black hover:bg-[#00c4ad] hover:shadow-[0_0_20px_rgba(0,217,192,0.4)] focus:ring-[#00D9C0]',
    secondary: 'bg-zinc-800 text-white border border-zinc-700 hover:bg-zinc-700 focus:ring-zinc-600'
  };
  if (href) {
    return <a href={href} className={`${baseStyles} ${variants[variant]} ${className}`} target="_blank" rel="noopener noreferrer" data-id="element-3">
        {children}
      </a>;
  }
  return <button className={`${baseStyles} ${variants[variant]} ${className}`} {...props} data-id="element-4">
      {children}
    </button>;
};
const Section = ({
  children,
  className = '',
  id = ''
}: {
  children: React.ReactNode;
  className?: string;
  id?: string;
}) => <section id={id} className={`py-20 md:py-32 px-6 ${className}`} data-id="element-5">
    <div className="max-w-4xl mx-auto" data-id="element-6">{children}</div>
  </section>;
// --- Modal Component ---
const Modal = ({
  isOpen,
  onClose,
  title,
  children
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}) => {
  return <AnimatePresence data-id="element-7">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[100]" data-id="element-8" />
          {/* Modal */}
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        duration: 0.2
      }} className="fixed inset-4 md:inset-auto md:left-1/2 md:top-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:max-w-2xl md:w-full md:max-h-[80vh] bg-zinc-900 border border-zinc-800 rounded-2xl z-[101] flex flex-col overflow-hidden" data-id="element-9">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-zinc-800" data-id="element-10">
              <h2 className="text-xl font-bold text-white" data-id="element-11">{title}</h2>
              <button onClick={onClose} className="w-8 h-8 rounded-full bg-zinc-800 flex items-center justify-center text-zinc-400 hover:text-white hover:bg-zinc-700 transition-colors" data-id="element-12">
                <X className="w-4 h-4" data-id="element-13" />
              </button>
            </div>
            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6 text-zinc-300 text-sm leading-relaxed space-y-4" data-id="element-14">
              {children}
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
};
// --- Main Page ---
export function SalesPage() {
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  const scrollToPricing = () => {
    document.getElementById('pricing')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <div className="min-h-screen bg-[#0a0a0a] text-zinc-100 font-sans selection:bg-[#00D9C0] selection:text-black overflow-x-hidden" data-id="element-15">
      {/* Privacy Policy Modal */}
      <Modal isOpen={privacyOpen} onClose={() => setPrivacyOpen(false)} title="Política de Privacidad" data-id="element-16">
        <p className="text-zinc-400 text-xs mb-4" data-id="element-17">
          Última actualización:{' '}
          {new Date().toLocaleDateString('es-ES', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        })}
        </p>

        <h3 className="text-white font-semibold text-base mt-6 mb-2" data-id="element-18">
          1. Información que Recopilamos
        </h3>
        <p data-id="element-19">
          Recopilamos información que nos proporcionas directamente al realizar
          una compra, incluyendo tu nombre, dirección de correo electrónico e
          información de pago. No almacenamos información de tarjetas de
          crédito; todos los pagos son procesados de forma segura por nuestros
          proveedores de pago.
        </p>

        <h3 className="text-white font-semibold text-base mt-6 mb-2" data-id="element-20">
          2. Uso de la Información
        </h3>
        <p data-id="element-21">Utilizamos la información recopilada para:</p>
        <ul className="list-disc list-inside ml-4 space-y-1 mt-2" data-id="element-22">
          <li data-id="element-23">Procesar y entregar tu compra</li>
          <li data-id="element-24">Enviarte actualizaciones sobre tu pedido</li>
          <li data-id="element-25">Responder a tus preguntas y solicitudes</li>
          <li data-id="element-26">
            Enviarte comunicaciones de marketing (solo si has dado tu
            consentimiento)
          </li>
        </ul>

        <h3 className="text-white font-semibold text-base mt-6 mb-2" data-id="element-27">
          3. Protección de Datos
        </h3>
        <p data-id="element-28">
          Implementamos medidas de seguridad técnicas y organizativas para
          proteger tu información personal contra acceso no autorizado, pérdida
          o alteración.
        </p>

        <h3 className="text-white font-semibold text-base mt-6 mb-2" data-id="element-29">
          4. Tus Derechos
        </h3>
        <p data-id="element-30">
          Tienes derecho a acceder, corregir o eliminar tu información personal.
          También puedes oponerte al procesamiento de tus datos o solicitar la
          portabilidad de los mismos.
        </p>

        <h3 className="text-white font-semibold text-base mt-6 mb-2" data-id="element-31">
          5. Cookies
        </h3>
        <p data-id="element-32">
          Utilizamos cookies esenciales para el funcionamiento del sitio y
          cookies analíticas para mejorar tu experiencia. Puedes gestionar tus
          preferencias de cookies en la configuración de tu navegador.
        </p>

        <h3 className="text-white font-semibold text-base mt-6 mb-2" data-id="element-33">
          6. Cambios a esta Política
        </h3>
        <p data-id="element-34">
          Podemos actualizar esta política ocasionalmente. Te notificaremos
          sobre cambios significativos publicando la nueva política en esta
          página.
        </p>
      </Modal>

      {/* Terms of Service Modal */}
      <Modal isOpen={termsOpen} onClose={() => setTermsOpen(false)} title="Términos y Condiciones" data-id="element-35">
        <p className="text-zinc-400 text-xs mb-4" data-id="element-36">
          Última actualización:{' '}
          {new Date().toLocaleDateString('es-ES', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        })}
        </p>

        <h3 className="text-white font-semibold text-base mt-6 mb-2" data-id="element-37">
          1. Aceptación de los Términos
        </h3>
        <p data-id="element-38">
          Al acceder y utilizar este sitio web y realizar una compra, aceptas
          estar sujeto a estos términos y condiciones de uso.
        </p>

        <h3 className="text-white font-semibold text-base mt-6 mb-2" data-id="element-39">
          2. Producto Digital
        </h3>
        <p data-id="element-40">
          "El Reset de 14 Días" es un producto digital (ebook) que se entrega
          electrónicamente. Una vez completada la compra, recibirás acceso
          inmediato al contenido.
        </p>

        <h3 className="text-white font-semibold text-base mt-6 mb-2" data-id="element-41">
          3. Uso Personal
        </h3>
        <p data-id="element-42">
          El contenido adquirido es para tu uso personal únicamente. No está
          permitido:
        </p>
        <ul className="list-disc list-inside ml-4 space-y-1 mt-2" data-id="element-43">
          <li data-id="element-44">Redistribuir, revender o compartir el contenido</li>
          <li data-id="element-45">Modificar o crear obras derivadas</li>
          <li data-id="element-46">Utilizar el contenido con fines comerciales sin autorización</li>
        </ul>

        <h3 className="text-white font-semibold text-base mt-6 mb-2" data-id="element-47">
          4. Política de Reembolso
        </h3>
        <p data-id="element-48">
          Debido a la naturaleza digital del producto, todas las ventas son
          finales. Sin embargo, si experimentas problemas técnicos para acceder
          al contenido, trabajaremos contigo para resolverlos.
        </p>

        <h3 className="text-white font-semibold text-base mt-6 mb-2" data-id="element-49">
          5. Descargo de Responsabilidad
        </h3>
        <p data-id="element-50">
          El contenido de este ebook es solo para fines informativos y
          educativos. No sustituye el consejo médico, psicológico o profesional.
          Consulta a un profesional de la salud antes de comenzar cualquier
          programa de ejercicio o bienestar.
        </p>

        <h3 className="text-white font-semibold text-base mt-6 mb-2" data-id="element-51">
          6. Limitación de Responsabilidad
        </h3>
        <p data-id="element-52">
          No garantizamos resultados específicos. Los resultados pueden variar
          según el individuo y su compromiso con el programa.
        </p>

        <h3 className="text-white font-semibold text-base mt-6 mb-2" data-id="element-53">
          7. Propiedad Intelectual
        </h3>
        <p data-id="element-54">
          Todo el contenido, incluyendo textos, gráficos y diseño, está
          protegido por derechos de autor y es propiedad exclusiva de El Reset
          de 14 Días.
        </p>

        <h3 className="text-white font-semibold text-base mt-6 mb-2" data-id="element-55">
          8. Modificaciones
        </h3>
        <p data-id="element-56">
          Nos reservamos el derecho de modificar estos términos en cualquier
          momento. Los cambios entrarán en vigor inmediatamente después de su
          publicación.
        </p>
      </Modal>

      {/* Sticky Mobile CTA */}
      <motion.div initial={{
      y: 100
    }} animate={{
      y: 0
    }} transition={{
      delay: 2
    }} className="fixed bottom-0 left-0 right-0 p-4 bg-[#0a0a0a]/90 backdrop-blur-md border-t border-zinc-800 z-50 md:hidden" data-id="element-57">
        <Button href="https://eficienciadigital.ca/cart/48274359353594:1" className="w-full py-3 text-base" data-id="element-58">
          Quiero el Reset →{' '}
          <span className="line-through text-zinc-600 mr-1" data-id="element-59">$12.99</span> $6.39
        </Button>
      </motion.div>

      {/* Navigation */}
      <nav className="fixed top-0 w-full z-40 bg-[#0a0a0a]/80 backdrop-blur-sm border-b border-zinc-900" data-id="element-60">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between" data-id="element-61">
          <span className="font-bold text-xl tracking-tight text-white" data-id="element-62">
            El Reset<span className="text-[#00D9C0]" data-id="element-63">.</span>
          </span>
          <button onClick={scrollToPricing} className="hidden md:block text-sm font-medium text-zinc-400 hover:text-[#00D9C0] transition-colors" data-id="element-64">
            Obtener acceso
          </button>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 md:pt-48 md:pb-32 px-6 overflow-hidden" data-id="element-65">
        {/* Background Gradients */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[600px] bg-[#00D9C0] opacity-[0.03] blur-[120px] rounded-full pointer-events-none" data-id="element-66" />

        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center relative z-10" data-id="element-67">
          <div className="space-y-8" data-id="element-68">
            <FadeIn data-id="element-69">
              <div className="inline-block px-3 py-1 rounded-full bg-zinc-900 border border-zinc-800 text-[#00D9C0] text-sm font-medium mb-4" data-id="element-70">
                Para personas con ansiedad y estrés alto
              </div>
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.1] tracking-tight text-white" data-id="element-71">
                No te falta disciplina. <br data-id="element-72" />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#00D9C0] to-teal-600" data-id="element-73">
                  Te falta un reset.
                </span>
              </h1>
            </FadeIn>

            <FadeIn delay={0.1} data-id="element-74">
              <p className="text-xl md:text-2xl text-zinc-400 font-light leading-relaxed" data-id="element-75">
                10 minutos al día para dejar de volver a cero.
              </p>
            </FadeIn>

            <FadeIn delay={0.2} data-id="element-76">
              <div className="space-y-6 text-lg text-zinc-300 border-l-2 border-zinc-800 pl-6 my-8" data-id="element-77">
                <p data-id="element-78">No es que seas flojo/a.</p>
                <p data-id="element-79">
                  Es que estás cansado/a… y cuando estás cansado/a, tu mente
                  empieza a negociar.
                </p>
                <p className="italic text-zinc-500" data-id="element-80">
                  "Hoy no." "Mañana con calma." "Si no puedo hacerlo bien, mejor
                  no hago nada."
                </p>
                <p data-id="element-81">
                  Y de repente estás otra vez en el punto donde te juras que el
                  lunes sí.
                </p>
                <p className="font-medium text-white" data-id="element-82">
                  Este no es otro plan "motivacional". Es un sistema pequeño y
                  repetible para los días en que tu ansiedad manda.
                </p>
              </div>
            </FadeIn>

            <FadeIn delay={0.3} data-id="element-83">
              <div className="flex flex-col sm:flex-row gap-4" data-id="element-84">
                <Button href="https://eficienciadigital.ca/cart/48274359353594:1" data-id="element-85">
                  Quiero el Reset →{' '}
                  <span className="line-through text-zinc-600 mr-1" data-id="element-86">
                    $12.99
                  </span>{' '}
                  $6.39
                </Button>
                <div className="flex items-center gap-2 text-sm text-zinc-500 px-4 py-2" data-id="element-87">
                  <Shield className="w-4 h-4" data-id="element-88" />
                  <span data-id="element-89">Pago único y seguro</span>
                </div>
              </div>
            </FadeIn>
          </div>

          <FadeIn delay={0.4} className="relative hidden md:block" data-id="element-90">
            <motion.div animate={{
            y: [0, -20, 0]
          }} transition={{
            duration: 6,
            repeat: Infinity,
            ease: 'easeInOut'
          }} className="relative z-10" data-id="element-91">
              <img src="https://cdn.magicpatterns.com/uploads/ovMysJcQmeAgzfRZRPLDCp/El_reset_de_14.png" alt="El Reset de 14 Días Ebook Cover" className="w-full max-w-md mx-auto rounded-lg shadow-2xl shadow-[#00D9C0]/10 rotate-[-3deg] hover:rotate-0 transition-transform duration-500" data-id="element-92" />
            </motion.div>
            {/* Decorative elements behind image */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-sm h-[400px] bg-zinc-800/50 rounded-2xl -rotate-6 -z-10 border border-zinc-700/50" data-id="element-93" />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-sm h-[400px] bg-zinc-900/50 rounded-2xl rotate-3 -z-20 border border-zinc-800/50" data-id="element-94" />
          </FadeIn>
        </div>
      </section>

      {/* Pain Section */}
      <Section className="bg-zinc-900/50 border-y border-zinc-900" data-id="element-95">
        <FadeIn data-id="element-96">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center" data-id="element-97">
            Si tu historia es: <br data-id="element-98" />
            <span className="text-zinc-500" data-id="element-99">"Empiezo fuerte… y me caigo"</span>
          </h2>
        </FadeIn>

        <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-100">
          <FadeIn className="space-y-6 text-lg text-zinc-300" data-id="element-101">
            <p data-id="element-102">Déjame adivinar cómo se siente.</p>
            <p data-id="element-103">
              Tú sí sabes qué hacer. Tú sí has visto rutinas. Tú sí te has
              inscrito al gym, has guardado videos, has comprado cositas.
            </p>
            <p className="font-medium text-white" data-id="element-104">
              El problema no es información.
            </p>
            <p data-id="element-105">
              El problema es el momento exacto en que el trabajo te exprime, la
              vida se complica, la ansiedad sube y tu mente te suelta la frase
              que te mata la semana:
            </p>
            <p className="text-2xl font-bold text-[#00D9C0]" data-id="element-106">
              "Ya no importa."
            </p>
          </FadeIn>

          <FadeIn delay={0.2} className="bg-zinc-950 p-8 rounded-2xl border border-zinc-800" data-id="element-107">
            <h3 className="text-sm font-bold text-zinc-500 uppercase tracking-wider mb-6" data-id="element-108">
              El Ciclo del Abandono
            </h3>
            <div className="space-y-4" data-id="element-109">
              {['Fallas un día', 'Llega la culpa', 'Te castigas', 'Abandonas', 'Vuelves a cero', 'El lunes te exiges el doble'].map((step, i) => <div key={i} className="flex items-center gap-4" data-id="element-110">
                  <div className="w-8 h-8 rounded-full bg-zinc-900 border border-zinc-800 flex items-center justify-center text-[#00D9C0] font-bold text-sm shrink-0" data-id="element-111">
                    {i + 1}
                  </div>
                  <span className="text-zinc-300" data-id="element-112">{step}</span>
                </div>)}
            </div>
            <p className="mt-8 text-sm text-zinc-500 italic border-t border-zinc-900 pt-4" data-id="element-113">
              Y cada reinicio duele más porque ya no solo estás peleando con el
              gym… Estás peleando con tu confianza.
            </p>
          </FadeIn>
        </div>
      </Section>

      {/* Why Fails Section */}
      <Section data-id="element-114">
        <div className="max-w-3xl mx-auto text-center mb-16" data-id="element-115">
          <FadeIn data-id="element-116">
            <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-117">
              Por qué "la disciplina" no te está funcionando
            </h2>
            <p className="text-xl text-zinc-400" data-id="element-118">
              La mayoría de planes están diseñados para tu mejor versión.
            </p>
          </FadeIn>
        </div>

        <div className="grid md:grid-cols-2 gap-8" data-id="element-119">
          <FadeIn className="bg-zinc-900 p-8 rounded-2xl border border-zinc-800 opacity-50" data-id="element-120">
            <div className="flex items-center gap-3 mb-6" data-id="element-121">
              <X className="text-red-500" data-id="element-122" />
              <h3 className="text-xl font-bold" data-id="element-123">Tu "Mejor Versión"</h3>
            </div>
            <ul className="space-y-3 text-zinc-400" data-id="element-124">
              <li data-id="element-125">• Cuando dormiste bien</li>
              <li data-id="element-126">• Cuando estás tranquilo/a</li>
              <li data-id="element-127">• Cuando tienes tiempo</li>
              <li data-id="element-128">• Cuando no estás ansioso/a</li>
            </ul>
          </FadeIn>

          <FadeIn delay={0.2} className="bg-gradient-to-b from-zinc-900 to-zinc-950 p-8 rounded-2xl border border-[#00D9C0]/30 shadow-[0_0_30px_rgba(0,217,192,0.05)] relative overflow-hidden" data-id="element-129">
            <div className="absolute top-0 left-0 w-full h-1 bg-[#00D9C0]" data-id="element-130" />
            <div className="flex items-center gap-3 mb-6" data-id="element-131">
              <Check className="text-[#00D9C0]" data-id="element-132" />
              <h3 className="text-xl font-bold text-white" data-id="element-133">Tus Días Reales</h3>
            </div>
            <p className="text-zinc-300 mb-4" data-id="element-134">
              Tú no compras un plan para tus días perfectos. Tú lo necesitas
              para tus días reales.
            </p>
            <p className="text-zinc-300" data-id="element-135">
              En tus días reales, tu sistema nervioso busca alivio rápido. Y el
              esfuerzo se siente como amenaza. Así que tu mente hace lo que
              sabe: <span className="text-[#00D9C0] font-bold" data-id="element-136">evita</span>.
            </p>
          </FadeIn>
        </div>
      </Section>

      {/* Solution Section */}
      <Section className="bg-zinc-900/30 border-y border-zinc-900" data-id="element-137">
        <div className="text-center mb-16" data-id="element-138">
          <FadeIn data-id="element-139">
            <h2 className="text-3xl md:text-5xl font-bold mb-6" data-id="element-140">
              La solución no es "más ganas". <br data-id="element-141" />
              Es un protocolo.
            </h2>
            <p className="text-xl text-zinc-400 max-w-2xl mx-auto" data-id="element-142">
              Presentando el{' '}
              <span className="text-[#00D9C0] font-bold" data-id="element-143">
                Micro-Reset Diario (10 min)
              </span>
              . Un sistema diseñado para cuando no tienes motivación.
            </p>
          </FadeIn>
        </div>

        <div className="grid md:grid-cols-3 gap-6" data-id="element-144">
          {[{
          icon: Brain,
          title: '1. Guion mental',
          time: '2 min',
          desc: 'Corta la culpa y la negociación interna antes de que te paralicen.'
        }, {
          icon: Zap,
          title: '2. Acción mínima',
          time: '6 min',
          desc: 'Movimiento simple, cero debate. Engañamos a la resistencia.'
        }, {
          icon: Shield,
          title: '3. Cierre de ansiedad',
          time: '2 min',
          desc: 'Sales con victoria, no con deuda. Reconstruyes confianza.'
        }].map((item, i) => <FadeIn key={i} delay={i * 0.1} className="bg-zinc-950 p-8 rounded-2xl border border-zinc-800 hover:border-[#00D9C0]/50 transition-colors group" data-id="element-145">
              <div className="w-12 h-12 rounded-full bg-zinc-900 flex items-center justify-center mb-6 text-[#00D9C0] group-hover:scale-110 transition-transform" data-id="element-146">
                <item.icon className="w-6 h-6" data-id="element-147" />
              </div>
              <div className="flex justify-between items-baseline mb-2" data-id="element-148">
                <h3 className="text-xl font-bold text-white" data-id="element-149">{item.title}</h3>
                <span className="text-xs font-mono text-zinc-500 border border-zinc-800 px-2 py-1 rounded" data-id="element-150">
                  {item.time}
                </span>
              </div>
              <p className="text-zinc-400 leading-relaxed" data-id="element-151">{item.desc}</p>
            </FadeIn>)}
        </div>

        <FadeIn delay={0.4} className="mt-12 text-center" data-id="element-152">
          <p className="text-lg text-zinc-300 mb-8" data-id="element-153">
            No es "hacer poco". Es construir evidencia diaria de que tú{' '}
            <span className="font-bold text-white" data-id="element-154">vuelves</span>.
          </p>
          <Button href="https://eficienciadigital.ca/cart/48274359353594:1" data-id="element-155">
            Empezar el Reset →{' '}
            <span className="line-through text-zinc-600 mr-1" data-id="element-156">$12.99</span>{' '}
            $6.39
          </Button>
        </FadeIn>
      </Section>

      {/* What Changes Section */}
      <Section data-id="element-157">
        <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-158">
          <FadeIn data-id="element-159">
            <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-160">
              Lo que cambia en 14 días
            </h2>
            <p className="text-zinc-400 mb-8" data-id="element-161">
              No te voy a vender fantasías. En 14 días, lo que cambia es esto:
            </p>
            <ul className="space-y-4" data-id="element-162">
              {['Vuelves más rápido cuando fallas', 'Discutes menos con tu cabeza', 'Te culpas menos', 'Haces lo mínimo sin drama', 'Recuperas una sensación que vale oro: CONTROL'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-163">
                  <Star className="w-5 h-5 text-[#00D9C0] shrink-0 fill-[#00D9C0]/20 mt-1" data-id="element-164" />
                  <span className="text-zinc-200 text-lg" data-id="element-165">{item}</span>
                </li>)}
            </ul>
            <p className="mt-8 text-zinc-500 text-sm" data-id="element-166">
              Y cuando esa base existe, ya puedes subir intensidad sin romperte.
            </p>
          </FadeIn>

          <FadeIn delay={0.2} className="relative" data-id="element-167">
            <div className="absolute inset-0 bg-gradient-to-tr from-[#00D9C0]/20 to-transparent rounded-2xl blur-2xl" data-id="element-168" />
            <div className="relative bg-zinc-900 border border-zinc-800 p-8 rounded-2xl" data-id="element-169">
              <div className="flex items-center gap-4 mb-6" data-id="element-170">
                <div className="w-12 h-12 rounded-full bg-zinc-800 flex items-center justify-center" data-id="element-171">
                  <Clock className="w-6 h-6 text-[#00D9C0]" data-id="element-172" />
                </div>
                <div data-id="element-173">
                  <div className="text-sm text-zinc-500" data-id="element-174">Tiempo diario</div>
                  <div className="text-xl font-bold text-white" data-id="element-175">10 Minutos</div>
                </div>
              </div>
              <div className="space-y-4" data-id="element-176">
                <div className="h-2 bg-zinc-800 rounded-full overflow-hidden" data-id="element-177">
                  <motion.div initial={{
                  width: 0
                }} whileInView={{
                  width: '100%'
                }} transition={{
                  duration: 1.5,
                  ease: 'easeOut'
                }} className="h-full bg-[#00D9C0]" data-id="element-178" />
                </div>
                <div className="flex justify-between text-sm text-zinc-400" data-id="element-179">
                  <span data-id="element-180">Día 1</span>
                  <span data-id="element-181">Día 14</span>
                </div>
                <p className="text-center text-white font-medium pt-4" data-id="element-182">
                  Consistencia {'>'} Intensidad
                </p>
              </div>
            </div>
          </FadeIn>
        </div>
      </Section>

      {/* For Who / Not For Who */}
      <Section className="bg-zinc-950" data-id="element-183">
        <div className="grid md:grid-cols-2 gap-8" data-id="element-184">
          <FadeIn className="bg-zinc-900/30 p-8 rounded-2xl border border-zinc-800" data-id="element-185">
            <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2" data-id="element-186">
              <Check className="text-[#00D9C0]" data-id="element-187" /> Esto es para ti si:
            </h3>
            <ul className="space-y-3" data-id="element-188">
              {['Estás en USA, con estrés real, vida real', "Te pasa '2 semanas sí, 2 no'", 'Te gana la ansiedad, la culpa o el cansancio', 'Quieres un sistema simple que no dependa de motivación'].map((item, i) => <li key={i} className="flex items-start gap-3 text-zinc-300" data-id="element-189">
                  <Check className="w-5 h-5 text-[#00D9C0] shrink-0 mt-0.5" data-id="element-190" />
                  <span data-id="element-191">{item}</span>
                </li>)}
            </ul>
          </FadeIn>

          <FadeIn delay={0.2} className="bg-zinc-900/30 p-8 rounded-2xl border border-zinc-800" data-id="element-192">
            <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2" data-id="element-193">
              <X className="text-zinc-500" data-id="element-194" /> Esto NO es para ti si:
            </h3>
            <ul className="space-y-3" data-id="element-195">
              {['Buscas un plan avanzado de hipertrofia', 'Quieres macros perfectas y detalles técnicos', 'Quieres una transformación extrema en 30 días'].map((item, i) => <li key={i} className="flex items-start gap-3 text-zinc-400" data-id="element-196">
                  <X className="w-5 h-5 text-zinc-600 shrink-0 mt-0.5" data-id="element-197" />
                  <span data-id="element-198">{item}</span>
                </li>)}
            </ul>
          </FadeIn>
        </div>
        <FadeIn className="text-center mt-8" data-id="element-199">
          <p className="text-zinc-400" data-id="element-200">
            Aquí ganamos por lo que la mayoría desprecia:{' '}
            <span className="text-white font-bold" data-id="element-201">lo mínimo sostenible.</span>
          </p>
        </FadeIn>
      </Section>

      {/* What's Included */}
      <Section data-id="element-202">
        <FadeIn data-id="element-203">
          <h2 className="text-3xl font-bold text-center mb-12" data-id="element-204">
            Qué incluye exactamente
          </h2>
        </FadeIn>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-205">
          {[{
          icon: BookOpen,
          text: 'Ebook El Reset de 14 Días (Ansiedad → Disciplina)'
        }, {
          icon: Zap,
          text: 'El sistema Micro-Reset Diario (10 min) explicado paso a paso'
        }, {
          icon: Menu,
          text: "'Puertas 1–3': qué hacer según tu nivel de energía/ansiedad"
        }, {
          icon: Clock,
          text: 'Plan de 14 días simple'
        }, {
          icon: Shield,
          text: 'Anti-caídas: cómo retomar sin reiniciar desde cero'
        }, {
          icon: Star,
          text: 'FAQ, glosario, recursos extra'
        }].map((item, i) => <FadeIn key={i} delay={i * 0.05} className="flex items-start gap-4 p-6 bg-zinc-900 rounded-xl border border-zinc-800" data-id="element-206">
              <item.icon className="w-6 h-6 text-[#00D9C0] shrink-0" data-id="element-207" />
              <span className="text-zinc-300 font-medium" data-id="element-208">{item.text}</span>
            </FadeIn>)}
        </div>
      </Section>

      {/* Pricing Section */}
      <Section id="pricing" className="bg-gradient-to-b from-zinc-900 to-black border-t border-zinc-800" data-id="element-209">
        <div className="max-w-2xl mx-auto text-center" data-id="element-210">
          <FadeIn data-id="element-211">
            <div className="inline-block px-4 py-1 rounded-full bg-[#00D9C0]/10 text-[#00D9C0] font-bold text-sm mb-6" data-id="element-212">
              OFERTA DE LANZAMIENTO — 50% OFF
            </div>
            <div className="flex items-center justify-center gap-4 mb-2" data-id="element-213">
              <span className="text-3xl md:text-4xl text-zinc-500 line-through font-medium" data-id="element-214">
                $12.99
              </span>
            </div>
            <h2 className="text-6xl md:text-8xl font-bold text-white mb-4 tracking-tighter" data-id="element-215">
              $6.39
            </h2>
            <p className="text-xl text-zinc-400 mb-8" data-id="element-216">
              Menos que una comida rápida para dos.
            </p>
            <p className="text-zinc-300 mb-10" data-id="element-217">
              Pero con un objetivo mucho más serio: que dejes de traicionarte
              cada semana.
            </p>

            <Button href="https://eficienciadigital.ca/cart/48274359353594:1" className="w-full md:w-auto text-xl px-12 py-5 shadow-[0_0_40px_rgba(0,217,192,0.3)] animate-pulse" data-id="element-218">
              Quiero el Reset ahora
            </Button>

            <div className="mt-6 flex items-center justify-center gap-4 text-sm text-zinc-500" data-id="element-219">
              <span className="flex items-center gap-1" data-id="element-220">
                <Shield className="w-3 h-3" data-id="element-221" /> Pago seguro
              </span>
              <span className="flex items-center gap-1" data-id="element-222">
                <Zap className="w-3 h-3" data-id="element-223" /> Entrega inmediata
              </span>
            </div>
          </FadeIn>
        </div>
      </Section>

      {/* Closing Section */}
      <Section className="pb-32" data-id="element-224">
        <div className="max-w-2xl mx-auto text-center space-y-8" data-id="element-225">
          <FadeIn data-id="element-226">
            <p className="text-2xl md:text-3xl font-light text-white leading-relaxed" data-id="element-227">
              No necesitas convertirte en otra persona. <br data-id="element-228" />
              Necesitas dejar de pelearte contigo cuando estás ansioso/a.
            </p>
          </FadeIn>

          <FadeIn delay={0.2} data-id="element-229">
            <p className="text-xl text-zinc-400" data-id="element-230">
              Hazlo fácil. Hazlo repetible. Hazlo hoy.
            </p>
          </FadeIn>

          <FadeIn delay={0.3} className="pt-8" data-id="element-231">
            <h3 className="text-4xl font-bold text-white mb-2" data-id="element-232">
              Elige 10 minutos.
            </h3>
            <p className="text-xl text-[#00D9C0]" data-id="element-233">
              Y gana la habilidad que te cambia la vida:{' '}
              <span className="font-bold underline decoration-2 underline-offset-4" data-id="element-234">
                volver
              </span>
              .
            </p>
          </FadeIn>
        </div>
      </Section>

      {/* Footer */}
      <footer className="py-12 border-t border-zinc-900" data-id="element-235">
        <div className="max-w-6xl mx-auto px-6" data-id="element-236">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6" data-id="element-237">
            <p className="text-zinc-600 text-sm" data-id="element-238">
              © {new Date().getFullYear()} El Reset de 14 Días. Todos los
              derechos reservados.
            </p>
            <div className="flex items-center gap-6" data-id="element-239">
              <button onClick={() => setPrivacyOpen(true)} className="text-zinc-500 hover:text-[#00D9C0] text-sm transition-colors" data-id="element-240">
                Política de Privacidad
              </button>
              <span className="text-zinc-800" data-id="element-241">|</span>
              <button onClick={() => setTermsOpen(true)} className="text-zinc-500 hover:text-[#00D9C0] text-sm transition-colors" data-id="element-242">
                Términos y Condiciones
              </button>
            </div>
          </div>
        </div>
      </footer>
    </div>;
}